import type Phaser from "phaser";
import { audioManager } from "../../../audio/AudioManager";
import { TORPEDO } from "../../../config";

// Sprite orientation offset: sprite points DOWN, Phaser rotation=0 is RIGHT
const SPRITE_ANGLE_OFFSET = Math.PI / 2;

export interface TorpedoSprite extends Phaser.Physics.Arcade.Sprite {
  lifespan: number;
}

export class TorpedoSystem {
  private scene: Phaser.Scene;
  private ship: Phaser.Physics.Arcade.Sprite;

  torpedoes!: Phaser.Physics.Arcade.Group;
  private lastFired = 0;

  constructor(scene: Phaser.Scene, ship: Phaser.Physics.Arcade.Sprite) {
    this.scene = scene;
    this.ship = ship;
  }

  create(): void {
    this.createTexture();
    this.torpedoes = this.scene.physics.add.group();
  }

  private createTexture(): void {
    if (this.scene.textures.exists("torpedo")) return;

    const graphics = this.scene.make.graphics();
    graphics.fillStyle(0xff6600);
    graphics.fillRect(0, 0, 12, 3);
    graphics.fillStyle(0xffaa00);
    graphics.fillRect(2, 1, 8, 1);
    graphics.generateTexture("torpedo", 12, 3);
    graphics.destroy();
  }

  fire(time: number): boolean {
    if (time <= this.lastFired + TORPEDO.DELAY) {
      return false;
    }

    audioManager.playShoot();

    const torpedo = this.torpedoes.create(
      this.ship.x,
      this.ship.y,
      "torpedo",
    ) as TorpedoSprite;

    // Torpedo fires in visual direction
    const visualRotation = this.ship.rotation + SPRITE_ANGLE_OFFSET;
    torpedo.rotation = visualRotation;

    const torpedoBody = torpedo.body as Phaser.Physics.Arcade.Body;
    const shipBody = this.ship.body as Phaser.Physics.Arcade.Body;

    this.scene.physics.velocityFromRotation(
      visualRotation,
      TORPEDO.SPEED,
      torpedoBody.velocity,
    );

    // Add ship velocity for realism
    torpedoBody.velocity.x += shipBody.velocity.x * 0.5;
    torpedoBody.velocity.y += shipBody.velocity.y * 0.5;

    torpedo.lifespan = TORPEDO.LIFESPAN;
    this.lastFired = time;

    return true;
  }

  update(
    delta: number,
    screenWrap: (sprite: Phaser.Physics.Arcade.Sprite) => void,
  ): void {
    this.torpedoes.children.each((torpedoObj) => {
      const torpedo = torpedoObj as TorpedoSprite;
      screenWrap(torpedo);
      torpedo.lifespan -= delta;
      if (torpedo.lifespan <= 0) {
        torpedo.destroy();
      }
      return true;
    });
  }

  destroy(): void {
    this.torpedoes.clear(true, true);
  }
}
